import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from 'react';

// Type definition for custom shape property
export interface CustomShapeProperty {
    id: string;
    name: string;
    type: 'color' | 'number' | 'text' | 'textContent';
    targetSelector: string;
    targetAttribute: string;
    defaultValue: string;
}

// Type definition for custom shape
export interface CustomShapeDefinition {
    id: string;
    name: string;
    description: string;
    svgData: string;
    properties: CustomShapeProperty[];
}

// Type definition for custom group (simplified for context)
export interface CustomGroupDefinition {
    id: string;
    name: string;
    description?: string;
    elements: any[]; // LayoutElement[] but keeping as any to avoid circular deps
}

// Context value type
export interface CustomShapeContextValue {
    customShapeDefinitions: CustomShapeDefinition[];
    setCustomShapeDefinitions: (definitions: CustomShapeDefinition[]) => void;
    customGroupDefinitions: CustomGroupDefinition[];
    setCustomGroupDefinitions: (definitions: CustomGroupDefinition[]) => void;
}

// Create context with default value
const CustomShapeContext = createContext<CustomShapeContextValue>({
    customShapeDefinitions: [],
    setCustomShapeDefinitions: () => {},
    customGroupDefinitions: [],
    setCustomGroupDefinitions: () => {},
});

// Provider component
interface CustomShapeProviderProps {
    children: React.ReactNode;
}

export function CustomShapeProvider({ children }: CustomShapeProviderProps): React.ReactElement {
    const [customShapeDefinitions, setDefinitions] = useState<CustomShapeDefinition[]>([]);
    const [customGroupDefinitions, setGroupDefinitions] = useState<CustomGroupDefinition[]>([]);

    const setCustomShapeDefinitions = useCallback((definitions: CustomShapeDefinition[]) => {
        setDefinitions(definitions);
    }, []);

    const setCustomGroupDefinitions = useCallback((definitions: CustomGroupDefinition[]) => {
        setGroupDefinitions(definitions);
    }, []);

    const value = useMemo(() => ({
        customShapeDefinitions,
        setCustomShapeDefinitions,
        customGroupDefinitions,
        setCustomGroupDefinitions,
    }), [
        customShapeDefinitions,
        setCustomShapeDefinitions,
        customGroupDefinitions,
        setCustomGroupDefinitions]);

    return (
        <CustomShapeContext.Provider value={value}>
            {children}
        </CustomShapeContext.Provider>
    );
}

// Hook to use the context
export function useCustomShapes(): CustomShapeContextValue {
    return useContext(CustomShapeContext);
}

export default CustomShapeContext;
